import React from "react";
import { Edit, Trash2, Calendar, User, MessageCircle } from "lucide-react";
import { LOCAL_ICONS } from "./counselingUiUtils";

export default function CounselingTimelineContent({
  studentLogs,
  isMobile,
  viewMode,
  getTypeColor,
  getTypeIcon,
  getLogIcon,
  setViewingLog,
  canEditLog,
  handleEditStart,
  handleDeleteLog,
  getSentimentIcon,
  editingLogId,
  timelineRef,
}) {
  return (
    <div className="flex-1 flex flex-col min-h-0 relative">
      <div className="flex-1 overflow-y-auto custom-scrollbar pt-6 px-8 pb-10" ref={timelineRef}>
        {studentLogs.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-400/60 pb-10 mt-10">
            <img src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/Objects/Memo.png" width={100} className="opacity-30 mb-6 grayscale" alt="empty" />
            <p className="text-xl font-black font-do-hyeon text-gray-400">아직 영혼 관리 여정이 시작되지 않았습니다.</p>
            <p className="text-sm font-bold text-gray-300 mt-2">따뜻한 첫 번째 기록을 남겨보세요! ✨</p>
          </div>
        ) : viewMode === "list" ? (
          <div className={`grid ${isMobile ? "grid-cols-2 gap-4" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6"} p-2`}>
            {studentLogs.map((log, index) => {
              const colors = getTypeColor(log.type);
              const isRecent = index === 0;

              return (
                <div
                  key={log.id}
                  onClick={() => setViewingLog(log)}
                  className={`group relative bg-white rounded-[2rem] p-4 shadow-[0_8px_30px_rgb(0,0,0,0.02)] flex flex-col items-center cursor-pointer border border-gray-100 hover:shadow-[0_15px_30px_rgb(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-500 overflow-hidden min-h-[180px]`}
                >
                  {/* Subtle Background Accent */}
                  <div className={`absolute top-0 right-0 w-20 h-20 -mr-10 -mt-10 rounded-full opacity-[0.05] transition-transform duration-700 group-hover:scale-150 ${colors.bg}`} />

                  {isRecent && (
                    <div className="absolute top-2 right-2 z-10">
                      <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 shadow-sm">
                        <span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse" />
                        <span className="text-[8px] font-black text-emerald-600">최신</span>
                      </div>
                    </div>
                  )}

                  <div className="w-full flex flex-col items-center gap-3 mb-4">
                    <span className="text-[10px] font-black text-gray-400 bg-gray-50 px-2.5 py-0.5 rounded-full border border-gray-100">
                      {log.date.slice(5).replace("-", ".")}
                    </span>

                    <div className="p-3 rounded-2xl bg-gray-50 flex items-center justify-center border border-gray-100 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-sm bg-gradient-to-br from-white to-gray-50">
                      <img src={getLogIcon(log.type)} alt={log.type} className="w-8 h-8 object-contain drop-shadow-sm" />
                    </div>
                  </div>

                  <div className="w-full mt-auto space-y-2">
                    <div className={`px-2 py-1.5 rounded-xl ${colors.bg} ${colors.text} shadow-sm border border-white/50 w-full flex items-center justify-center`}>
                      <span className="text-[11px] font-black truncate">{log.type}</span>
                    </div>
                    {log.createdByName && (
                      <div className="flex items-center justify-center gap-1 text-gray-400 group-hover:text-gray-500 transition-colors">
                        <User size={10} className="opacity-50" />
                        <span className="text-[9px] font-bold truncate tracking-tight">{log.createdByName}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-10 relative max-w-3xl mx-auto">
            {/* Timeline Continuous Line */}
            <div className={`absolute ${isMobile ? "left-[23px]" : "left-[31px]"} top-8 bottom-8 w-1 bg-gradient-to-b from-gray-100 via-gray-100 to-transparent rounded-full opacity-50`}></div>

            {studentLogs.map((log) => {
              const colors = getTypeColor(log.type);
              return (
                <div key={log.id} className={`relative ${isMobile ? "pl-14" : "pl-20"} group animate-in fade-in slide-in-from-left-4 duration-500`}>
                  {/* Icon Node */}
                  <div className={`absolute left-0 top-0 ${isMobile ? "w-12 h-12 rounded-[1.2rem]" : "w-16 h-16 rounded-[1.8rem]"} bg-white border border-gray-100 shadow-[0_8px_30px_rgba(0,0,0,0.03)] z-10 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                    <img src={getLogIcon(log.type)} width={isMobile ? 24 : 32} alt="icon" className="drop-shadow-sm" />
                  </div>

                  {/* Card Content */}
                  <div className={`rounded-[2.5rem] p-6 sm:p-8 border border-gray-100 transition-all duration-500 relative ${log.id === editingLogId ? "bg-emerald-50 border-emerald-200 ring-4 ring-emerald-100 shadow-xl" : "bg-white/60 hover:bg-white hover:shadow-[0_15px_30px_rgba(0,0,0,0.06)] hover:-translate-y-1 shadow-sm"}`}>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className={`px-4 py-1.5 rounded-full text-[11px] font-black shadow-sm ${colors.bg} ${colors.text} border border-white/50 flex items-center gap-2`}>
                          <MessageCircle size={12} /> {log.type}
                        </div>
                        <div className="flex items-center gap-2 text-gray-400 font-bold bg-gray-50 border border-gray-100 px-3 py-1.5 rounded-full text-xs">
                          <Calendar size={12} /> {log.date}
                        </div>
                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-gray-50">
                          {getSentimentIcon(log.sentiment)}
                        </div>
                      </div>

                      <div className="flex gap-1.5">
                        {canEditLog(log) && (
                          <>
                            <button onClick={() => handleEditStart(log)} className="text-gray-300 hover:text-gray-900 hover:bg-gray-100 p-2.5 rounded-2xl transition-all shadow-sm bg-white border border-gray-50">
                              <Edit size={16} />
                            </button>
                            <button onClick={() => handleDeleteLog(log.id)} className="text-gray-300 hover:text-rose-500 hover:bg-rose-50 p-2.5 rounded-2xl transition-all shadow-sm bg-white border border-gray-50">
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <p className={`text-gray-800 leading-[1.8] font-bold whitespace-pre-wrap ${isMobile ? "text-sm" : "text-base"} relative z-10 px-1`}>
                      {log.content}
                    </p>

                    {log.createdByName && (
                      <div className="mt-6 pt-5 border-t border-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[11px] sm:text-xs text-gray-400 font-black tracking-tight">
                          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                            <User size={12} className="text-gray-400" />
                          </div>
                          <span>{log.createdByName} 선생님</span>
                        </div>
                        {log.createdAt && (
                          <span className="text-[10px] font-bold text-gray-300">
                            등록일: {new Date(log.createdAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
